import React from 'react';
import { motion } from 'framer-motion';
import { Home, Plus, Minus, Trash2 } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import { formatRoomName } from '../../utils/formatters';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * Rooms component
 * Third step in the wireless advisor form
 * Allows users to specify the rooms in their home
 */
const Rooms = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Common room types
  const roomTypes = [
    'livingRoom',
    'bedroom',
    'kitchen',
    'diningRoom',
    'bathroom',
    'office',
    'gameRoom',
    'otherRoom'
  ];
  
  // Handle room count change
  const handleRoomCountChange = (roomType, change) => {
    const currentCount = formData.rooms[roomType] || 0;
    const newCount = Math.max(0, currentCount + change);
    
    // Update form data
    updateFormData({
      rooms: {
        ...formData.rooms,
        [roomType]: newCount
      }
    });
    
    // Track event
    trackEvent('wireless_advisor_room_count_changed', {
      room_type: roomType,
      count: newCount
    });
  };
  
  // Get total room count
  const getTotalRoomCount = () => {
    return Object.values(formData.rooms).reduce((sum, count) => sum + count, 0);
  };
  
  // Get room icon
  const getRoomIcon = (roomType) => {
    return <Home className="h-5 w-5" />;
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">What rooms are in your home?</h2>
        <QuestionTooltip 
          content="Tell us about the rooms in your home to help us understand your layout and calculate optimal wireless coverage."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        Select the number of each type of room in your home. This helps us understand your layout and connectivity needs.
      </p>
      
      <div className="space-y-4">
        {roomTypes.map((roomType) => (
          <motion.div
            key={roomType}
            className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
            whileHover={{ backgroundColor: 'rgba(75, 85, 99, 0.3)' }}
          >
            <div className="flex items-center">
              <div className="p-2 bg-gray-700 rounded-lg mr-3">
                {getRoomIcon(roomType)}
              </div>
              <span className="font-medium text-white">{formatRoomName(roomType)}</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                className="p-1 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
                onClick={() => handleRoomCountChange(roomType, -1)}
                disabled={!formData.rooms[roomType]}
              >
                <Minus className={`h-5 w-5 ${formData.rooms[roomType] ? 'text-white' : 'text-gray-500'}`} />
              </button>
              
              <span className="w-8 text-center font-medium text-white">
                {formData.rooms[roomType] || 0}
              </span>
              
              <button
                className="p-1 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors"
                onClick={() => handleRoomCountChange(roomType, 1)}
              >
                <Plus className="h-5 w-5 text-white" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      
      {getTotalRoomCount() > 0 && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            {getTotalRoomCount()} {getTotalRoomCount() === 1 ? 'Room' : 'Rooms'} Selected
          </h3>
          <p className="text-sm text-gray-300">
            Based on your selection, we'll analyze coverage needs for each room type, prioritizing areas where you need the strongest connection.
          </p>
          
          {/* Room summary */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {roomTypes.map((roomType) => {
              const count = formData.rooms[roomType] || 0;
              if (count === 0) return null;
              
              return (
                <div key={roomType} className="flex items-center text-sm text-gray-300">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                  <span>{formatRoomName(roomType)}: {count}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
      
      {getTotalRoomCount() === 0 && (
        <div className="mt-6 p-4 bg-amber-600/10 border border-amber-600/20 rounded-lg">
          <p className="text-sm text-amber-300">
            Please select at least one room to continue.
          </p>
        </div>
      )}
    </div>
  );
};

export default Rooms;